'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import InnerPageHeader from '@/components/InnerPageHeader';
import Toast from '@/components/Toast';
import '@/styles/global.css';

export default function DashboardPage() {
  const router = useRouter();
  const [user, setUser] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editedUser, setEditedUser] = useState({
    fullName: '',
    email: '',
    phone: ''
  });
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      router.push('/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    setEditedUser({
      fullName: parsedUser.fullName || '',
      email: parsedUser.email || '',
      phone: parsedUser.phone || ''
    });

    // Load profile picture from localStorage
    const savedPicture = localStorage.getItem('profilePicture');
    if (savedPicture) {
      setProfilePicture(savedPicture);
    }
  }, [router]);

  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert('File size should be less than 2MB');
        return;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setProfilePicture(base64String);
        localStorage.setItem('profilePicture', base64String);
        
        // Show success toast
        setToast({ message: 'Profile picture updated successfully!', type: 'success' });
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    // TODO: API call to change password
    // For now, just show success message
    setToast({ message: 'Password changed successfully!', type: 'success' });
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setIsEditingPassword(false);
    setPasswordError('');
  };

  const handleProfileSave = () => {
    // Validation
    if (!editedUser.fullName.trim()) {
      setToast({ message: 'Full name is required', type: 'error' });
      return;
    }

    if (!editedUser.email.trim()) {
      setToast({ message: 'Email is required', type: 'error' });
      return;
    }

    if (!editedUser.phone.trim()) {
      setToast({ message: 'Phone number is required', type: 'error' });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editedUser.email)) {
      setToast({ message: 'Please enter a valid email address', type: 'error' });
      return;
    }

    // Phone validation
    const phoneRegex = /^[0-9\s]{10,}$/;
    if (!phoneRegex.test(editedUser.phone)) {
      setToast({ message: 'Please enter a valid phone number', type: 'error' });
      return;
    }

    // Update user data
    const updatedUser = {
      ...user,
      fullName: editedUser.fullName,
      email: editedUser.email,
      phone: editedUser.phone
    };

    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setIsEditingProfile(false);
    
    // Show success toast
    setToast({ message: 'Profile updated successfully!', type: 'success' });
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('profilePicture');
    router.push('/');
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#F6EFED] flex items-center justify-center">
        <p className="text-[18px] text-[#2E2A3A]">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6EFED]">
      <InnerPageHeader />

      <div className="max-w-[1380px] mx-auto px-[20px] py-20">
        <div className="grid md:grid-cols-[280px_1fr] gap-8">
          {/* Sidebar */}
          <aside className="bg-white rounded-[24px] p-8 h-fit">
            {/* User Profile */}
            <div className="text-center mb-8">
              <div className="relative w-24 h-24 mx-auto mb-4">
                {profilePicture ? (
                  <img
                    src={profilePicture}
                    alt="Profile"
                    className="w-24 h-24 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-24 h-24 bg-[#8A7296] rounded-full flex items-center justify-center">
                    <span className="text-white text-[32px] font-['Gotham_Rounded',sans-serif] font-bold">
                      {user.fullName?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <label
                  htmlFor="profile-upload"
                  className="absolute bottom-0 right-0 w-8 h-8 bg-[#8A7296] rounded-full flex items-center justify-center cursor-pointer hover:bg-[#7a647d] transition-colors"
                >
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <input
                    id="profile-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePictureChange}
                    className="hidden"
                  />
                </label>
              </div>
              <h2 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A] mb-1">
                {user.fullName}
              </h2>
              <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">
                {user.email}
              </p>
            </div>

            {/* Navigation */}
            <nav className="space-y-2">
              <button
                onClick={() => setActiveTab('overview')}
                className={`w-full text-left px-4 py-3 rounded-lg font-['Archivo',sans-serif] text-[16px] transition-colors ${
                  activeTab === 'overview'
                    ? 'bg-[#8A7296] text-white'
                    : 'text-[#2E2A3A] hover:bg-[#F6EFED]'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('saved')}
                className={`w-full text-left px-4 py-3 rounded-lg font-['Archivo',sans-serif] text-[16px] transition-colors ${
                  activeTab === 'saved'
                    ? 'bg-[#8A7296] text-white'
                    : 'text-[#2E2A3A] hover:bg-[#F6EFED]'
                }`}
              >
                Saved Places
              </button>
              <button
                onClick={() => setActiveTab('profile')}
                className={`w-full text-left px-4 py-3 rounded-lg font-['Archivo',sans-serif] text-[16px] transition-colors ${
                  activeTab === 'profile'
                    ? 'bg-[#8A7296] text-white'
                    : 'text-[#2E2A3A] hover:bg-[#F6EFED]'
                }`}
              >
                My Profile
              </button>
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-3 rounded-lg font-['Archivo',sans-serif] text-[16px] text-red-600 hover:bg-red-50 transition-colors"
              >
                Logout
              </button>
            </nav>
          </aside>

          {/* Main Content */}
          <main>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div>
                <h1 className="font-['Gotham_Rounded',sans-serif] text-[36px] font-bold text-[#2E2A3A] mb-8">
                  Welcome back, {user.fullName?.split(' ')[0]}! 👋
                </h1>

                {/* Stats Cards */}
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-white rounded-[20px] p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#8A7296]/10 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-[#8A7296]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">Saved Gyms</p>
                        <p className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A]">0</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-[20px] p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#8A7296]/10 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-[#8A7296]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">Saved Trainers</p>
                        <p className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A]">0</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-[20px] p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#8A7296]/10 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-[#8A7296]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">Reviews</p>
                        <p className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A]">0</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-[24px] p-8">
                  <h2 className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A] mb-6">
                    Quick Actions
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Link
                      href="/search-process"
                      className="p-6 border-2 border-[#8A7296]/20 rounded-[16px] hover:border-[#8A7296] hover:bg-[#8A7296]/5 transition-all group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#8A7296] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-['Gotham_Rounded',sans-serif] text-[18px] font-bold text-[#2E2A3A]">
                            Find Gyms
                          </h3>
                          <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">
                            Discover women-only fitness spaces
                          </p>
                        </div>
                      </div>
                    </Link>

                    <Link
                      href="/search-process"
                      className="p-6 border-2 border-[#8A7296]/20 rounded-[16px] hover:border-[#8A7296] hover:bg-[#8A7296]/5 transition-all group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#8A7296] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-['Gotham_Rounded',sans-serif] text-[18px] font-bold text-[#2E2A3A]">
                            Find Trainers
                          </h3>
                          <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">
                            Connect with certified trainers
                          </p>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Saved Places Tab */}
            {activeTab === 'saved' && (
              <div>
                <h1 className="font-['Gotham_Rounded',sans-serif] text-[36px] font-bold text-[#2E2A3A] mb-8">
                  Saved Places
                </h1>
                <div className="bg-white rounded-[24px] p-12 text-center">
                  <div className="w-20 h-20 bg-[#8A7296]/10 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <svg className="w-10 h-10 text-[#8A7296]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                    </svg>
                  </div>
                  <h3 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A] mb-2">
                    No saved places yet
                  </h3>
                  <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672] mb-6">
                    Start exploring and save your favorite gyms and trainers
                  </p>
                  <Link
                    href="/search-process"
                    className="inline-block px-8 py-3 bg-[#8A7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors"
                  >
                    Explore Now
                  </Link>
                </div>
              </div>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div>
                <h1 className="font-['Gotham_Rounded',sans-serif] text-[36px] font-bold text-[#2E2A3A] mb-8">
                  My Profile
                </h1>
                
                {/* Profile Information */}
                <div className="bg-white rounded-[24px] p-8 mb-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A]">
                      Profile Information
                    </h2>
                    {!isEditingProfile && (
                      <button
                        onClick={() => setIsEditingProfile(true)}
                        className="px-4 py-2 bg-[#8A7296] text-white font-['Archivo',sans-serif] text-[14px] font-medium rounded-[8px] hover:bg-[#7a647d] transition-colors"
                      >
                        Edit Profile
                      </button>
                    )}
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={isEditingProfile ? editedUser.fullName : user.fullName}
                        onChange={(e) => setEditedUser({ ...editedUser, fullName: e.target.value })}
                        readOnly={!isEditingProfile}
                        className={`w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px] ${
                          isEditingProfile ? 'bg-white' : 'bg-[#F6EFED]/50'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={isEditingProfile ? editedUser.email : user.email}
                        onChange={(e) => setEditedUser({ ...editedUser, email: e.target.value })}
                        readOnly={!isEditingProfile}
                        className={`w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px] ${
                          isEditingProfile ? 'bg-white' : 'bg-[#F6EFED]/50'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={isEditingProfile ? editedUser.phone : user.phone}
                        onChange={(e) => setEditedUser({ ...editedUser, phone: e.target.value })}
                        readOnly={!isEditingProfile}
                        className={`w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px] ${
                          isEditingProfile ? 'bg-white' : 'bg-[#F6EFED]/50'
                        }`}
                      />
                    </div>
                    {isEditingProfile && (
                      <div className="flex gap-3 pt-2">
                        <button
                          onClick={handleProfileSave}
                          className="px-6 py-3 bg-[#8A7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors"
                        >
                          Save Changes
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingProfile(false);
                            setEditedUser({
                              fullName: user.fullName,
                              email: user.email,
                              phone: user.phone
                            });
                          }}
                          className="px-6 py-3 border border-[#d7d7d7] text-[#2E2A3A] font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#F6EFED] transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Change Password Section */}
                <div className="bg-white rounded-[24px] p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A]">
                      Change Password
                    </h2>
                    {!isEditingPassword && (
                      <button
                        onClick={() => setIsEditingPassword(true)}
                        className="px-4 py-2 bg-[#8A7296] text-white font-['Archivo',sans-serif] text-[14px] font-medium rounded-[8px] hover:bg-[#7a647d] transition-colors"
                      >
                        Change Password
                      </button>
                    )}
                  </div>

                  {isEditingPassword ? (
                    <form onSubmit={handlePasswordChange} className="space-y-4">
                      {passwordError && (
                        <div className="p-3 bg-red-100 text-red-700 rounded-lg font-['Archivo',sans-serif] text-[14px]">
                          {passwordError}
                        </div>
                      )}

                      <div>
                        <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                          Current Password
                        </label>
                        <input
                          type="password"
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                          className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px]"
                          placeholder="Enter current password"
                        />
                      </div>

                      <div>
                        <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                          New Password
                        </label>
                        <input
                          type="password"
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                          className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px]"
                          placeholder="Enter new password (min 8 characters)"
                        />
                      </div>

                      <div>
                        <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                          Confirm New Password
                        </label>
                        <input
                          type="password"
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                          className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[10px] font-['Archivo',sans-serif] text-[16px]"
                          placeholder="Confirm new password"
                        />
                      </div>

                      <div className="flex gap-3 pt-2">
                        <button
                          type="submit"
                          className="px-6 py-3 bg-[#8A7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors"
                        >
                          Update Password
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditingPassword(false);
                            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                            setPasswordError('');
                          }}
                          className="px-6 py-3 border border-[#d7d7d7] text-[#2E2A3A] font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#F6EFED] transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">
                      Click "Change Password" to update your password
                    </p>
                  )}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
